import React from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "sonner";

import { AuthProvider } from "@/contexts/AuthContext";
import { I18nProvider } from "@/contexts/I18nContext";
import { PublicLayout } from "@/layouts/PublicLayout";
import { PortalLayout } from "@/layouts/PortalLayout";
import { ProtectedRoute } from "@/components/ProtectedRoute";

import Home from "@/pages/public/Home";
import Contact from "@/pages/public/Contact";
import Team from "@/pages/public/Team";
import LegalPage from "@/pages/public/LegalPage";
import RiskWarning from "@/pages/public/RiskWarning";

import InsightHome from "@/pages/insight/InsightHome";
import InsightRequest from "@/pages/insight/InsightRequest";
import GpuLanding from "@/pages/gpu/GpuLanding";

import { Login, Register } from "@/pages/auth/AuthPages";

import Dashboard from "@/pages/portal/Dashboard";
import UserDocuments from "@/pages/portal/UserDocuments";
import Simulator from "@/pages/portal/Simulator";
import Support from "@/pages/portal/Support";
import Subscription from "@/pages/portal/Subscription";
import Profile from "@/pages/portal/Profile";
import PortalTeam from "@/pages/portal/Team";

import { AdminDashboard, AdminAudit, AdminContact } from "@/pages/admin/AdminPages";
import AdminUsers from "@/pages/admin/AdminUsers";
import AdminReels from "@/pages/admin/AdminReels";

const wrapPublic = (el) => <PublicLayout>{el}</PublicLayout>;
const wrapPortal = (el) => <ProtectedRoute><PortalLayout>{el}</PortalLayout></ProtectedRoute>;
const wrapAdmin  = (el) => <ProtectedRoute adminOnly><PortalLayout admin>{el}</PortalLayout></ProtectedRoute>;

function App() {
  return (
    <I18nProvider>
      <AuthProvider>
        <div className="App">
          <BrowserRouter>
            <Routes>
              {/* Public */}
              <Route path="/" element={wrapPublic(<Home />)} />
              <Route path="/contact"      element={wrapPublic(<Contact />)} />
              <Route path="/team"         element={wrapPublic(<Team />)} />
              <Route path="/risk-warning" element={wrapPublic(<RiskWarning />)} />
              <Route path="/legal"        element={wrapPublic(<LegalPage slug="legal" />)} />
              <Route path="/privacy"      element={wrapPublic(<LegalPage slug="privacy" />)} />
              <Route path="/cookies"      element={wrapPublic(<LegalPage slug="cookies" />)} />
              <Route path="/terms"        element={wrapPublic(<LegalPage slug="terms" />)} />

              {/* Insight funnel — whitelabel, editorial, noindex. NO Titan branding until the user actively contacts us. */}
              <Route path="/insight"                       element={<InsightHome />} />
              <Route path="/insight/request-information"   element={<InsightRequest />} />

              {/* GPU rental landing — Instagram cold traffic (FR/BE/CH), noindex, mobile-first */}
              <Route path="/gpu"                           element={<GpuLanding />} />

              {/* Auth */}
              <Route path="/login"    element={<Login />} />
              <Route path="/register" element={<Register />} />

              {/* Client space */}
              <Route path="/portal"              element={wrapPortal(<Dashboard />)} />
              <Route path="/portal/subscription" element={wrapPortal(<Subscription />)} />
              <Route path="/portal/documents"    element={wrapPortal(<UserDocuments />)} />
              <Route path="/portal/simulator"    element={wrapPortal(<Simulator />)} />
              <Route path="/portal/support"      element={wrapPortal(<Support />)} />
              <Route path="/portal/team"         element={wrapPortal(<PortalTeam />)} />
              <Route path="/portal/profile"      element={wrapPortal(<Profile />)} />

              {/* Admin */}
              <Route path="/admin"          element={wrapAdmin(<AdminDashboard />)} />
              <Route path="/admin/users"    element={wrapAdmin(<AdminUsers />)} />
              <Route path="/admin/reels"    element={wrapAdmin(<AdminReels />)} />
              <Route path="/admin/contact"  element={wrapAdmin(<AdminContact />)} />
              <Route path="/admin/audit"    element={wrapAdmin(<AdminAudit />)} />

              {/* Retired public / portal routes → send to home or portal */}
              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
          </BrowserRouter>
          <Toaster theme="dark" position="top-right" toastOptions={{ style: { fontFamily: "General Sans, IBM Plex Sans, sans-serif" } }} />
        </div>
      </AuthProvider>
    </I18nProvider>
  );
}

export default App;
